import addressFormatter from '@fragaria/address-formatter';

import { Address } from '@/core/graphql/generated/types';

import { getCountryCode } from './addresses/getCountryCode';
import { isIntlDiff } from './isValidIntl';

type Options = {
	withInternational?: boolean;
	view?: 'basic' | 'summary' | 'full' | 'all';
	includeCountry?: boolean;
};

// Swapped in for fields we still hand to the formatter (so country-specific
// templates resolve to the right shape) but don't want to display. Wrapped in
// Unicode private-use characters so it can never collide with real address
// content.
const REDACTED = 'ADDR_REDACTED';

const withIntlSuffix = (
	primary: string | null | undefined,
	intl: string | null | undefined,
	withInternational: boolean
): string | undefined => {
	if (!primary) return undefined;
	if (!withInternational || !intl) return primary;
	return isIntlDiff(primary, intl) ? `${primary} (${intl})` : primary;
};

const displayValue = (
	value: string | undefined,
	include: boolean
): string | undefined => (!value ? undefined : include ? value : REDACTED);

export const getAddressParts = (
	address: Address,
	{
		withInternational = true,
		view = 'full',
		includeCountry = false
	}: Options = {}
): string[] => {
	if (!address) return [];

	const includeStreet = view !== 'basic';
	const includeStreet2 = view === 'full' || view === 'all';
	const includePostcode = view !== 'basic';
	const includeAll = view === 'all';

	const street1 = withIntlSuffix(
		address.streetAddress1Cda_V,
		address.streetAddress1Intl_V,
		withInternational
	);

	const street2 = withIntlSuffix(
		address.streetAddress2Cda_V,
		address.streetAddress2Intl_V,
		withInternational
	);

	const city = withIntlSuffix(
		address.cityCda_V,
		address.cityIntl_V,
		withInternational
	);

	const postcode = withIntlSuffix(
		address.postalCodeCda_V,
		address.postalCodeIntl_V,
		withInternational
	);

	const countryCode = getCountryCode(address.countryCda_V);
	const countryDisplay = includeCountry
		? countryCode === 'US'
			? 'US'
			: (address.countryCda_V ?? undefined)
		: undefined;

	// Street/postcode are always handed to the formatter, even when the view
	// won't display them, because several country templates only resolve to
	// the right shape when those fields are present (e.g. Germany drops the
	// state line only when postcode/city are populated). Fields the view
	// excludes are swapped for REDACTED and scrubbed from the output below,
	// instead of omitted from the input.
	const road = [
		displayValue(street1, includeStreet),
		displayValue(street2, includeStreet2)
	]
		.filter(Boolean)
		.join('\n');

	const postcodeForFormat = displayValue(postcode, includePostcode);

	const rawLines = addressFormatter
		.format(
			{
				...(road && { road }),
				...(city && { city }),
				...(address.stateCda_V && {
					state: address.stateCda_V,
					state_code: address.stateCda_V
				}),
				...(postcodeForFormat && { postcode: postcodeForFormat }),
				...(countryCode && { countryCode }),
				...(countryDisplay && { country: countryDisplay })
			},
			{
				output: 'array',
				abbreviate: false,
				appendCountry: includeCountry,
				cleanupPostcode: false
			}
		)
		.map(line => line.trim())
		.filter(Boolean);

	// Drop the REDACTED placeholder and tidy up whatever separator the
	// template glued it to, e.g. "Gurugram - REDACTED" -> "Gurugram -", or a
	// placeholder sandwiched between two kept fields ("Gurugram REDACTED,
	// Haryana" -> "Gurugram , Haryana"). Values are removed first and
	// separators normalized after, rather than removing the value plus its
	// neighbouring separators in one step, so a placeholder sitting between
	// two kept fields still ends up joined by exactly one separator instead
	// of none.
	const strippedLines = rawLines
		.map(line => line.split(REDACTED).join(''))
		.map(line =>
			line
				.replace(/\s+/g, ' ')
				.replace(/\s*,\s*/g, ', ')
				.replace(/(,\s*){2,}/g, ', ')
				.replace(/^[-\s,./:]+|[-\s,./:]+$/g, '')
		)
		.filter(line => /[a-zA-Z0-9]/.test(line));

	const lines =
		countryCode === 'US' && countryDisplay
			? strippedLines.map(line =>
					line === 'United States of America' ? countryDisplay : line
				)
			: strippedLines;

	if (includeAll) {
		if (address.postalCodeFullUs_V) lines.push(address.postalCodeFullUs_V);
		if (address.subStateAdminUnit_V) lines.push(address.subStateAdminUnit_V);
		if (address.cbsa_V) lines.push(address.cbsa_V);
	}

	return lines;
};
